import { Loader2 } from 'lucide-react'
import Link from 'next/link'
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  LibraryFilters,
  LibraryPager,
  LibraryRow,
  LibrarySearchButton,
  LibrarySearchInput,
  LibraryTabs,
  libraryPanelId,
  libraryTabId,
} from '~/src/components/library/LibraryParts'
import {
  BOTTLE_LISTS,
  LIBRARY_FILTER_LABELS,
  LIBRARY_TABS,
  LIBRARY_TAB_FILTERS,
  LIBRARY_CATEGORY_FILTERS,
  type LibraryCategoryFilter,
  type LibraryEntry,
  type LibraryFilter,
  type LibraryTabId,
  bottleEntries,
  libraryCount,
  matchesFilter,
  recentEntry,
  saveEntry,
} from '~/src/components/library/libraryModel'
import {
  isSearching,
  searchItems,
} from '~/src/components/library/librarySearch'
import useCaptureUTMParams from '~/src/hooks/useCaptureUTMParams'
import type { ProfileBookmark } from '~/src/hooks/useFetchProfileData'
import type { UnifiedRecentItem } from '~/src/hooks/useFetchRecents'
import type { LibraryBottle } from '~/src/hooks/useLibraryBottles'
import { convertToRedirectUrl } from '~/src/lib/helpers'
import { cn } from '~/src/lib/utils'

export interface LibraryLists {
  recents: UnifiedRecentItem[]
  /** More recents than loaded so far. */
  recentsMore: boolean
  saves: ProfileBookmark[]
  /** More saves than read (the api sends at most 50). */
  savesMore: boolean
  loved: LibraryBottle[]
  owned: LibraryBottle[]
  wanted: LibraryBottle[]
}

/** One category's recents, read filtered from the api. */
export interface RecentsByCategory {
  category: LibraryCategoryFilter
  recents: UnifiedRecentItem[]
  more: boolean
  loadMore: () => void
  loading: boolean
}

/** Every recent (up to a cap) read for a search, as the pages arrive. */
export interface RecentsSearch {
  recents: UnifiedRecentItem[]
  loading: boolean
  /** Stopped at the cap with more unread: "Searched your latest 200". */
  capped: boolean
  /** A page failed to load, so the search didn't read every recent. */
  failed: boolean
  retry: () => void
}

/** The lists plus reading more recents: what `useLibraryNav` hands the menus. */
export type LibrarySource = LibraryLists & {
  loadMoreRecents: () => void
  loadingMoreRecents: boolean
  recentsByCategory?: RecentsByCategory | null
  setRecentsCategory?: (category: LibraryCategoryFilter | null) => void
  recentsSearch?: RecentsSearch | null
  setSearchingRecents?: (on: boolean) => void
}

const MAX_FILTER_LOADS = 3

/** Where the list is scrolled, in rows. */
interface ListView {
  rowHeight: number
  /** Whole rows that fit: the arrows scroll this many. */
  perPage: number
  /** Rows at least half in view: what the "1–10" label counts. */
  inView: number
  /** The first row in view, 0-based. */
  first: number
  atTop: boolean
  nearEnd: boolean
  atEnd: boolean
}

const NOUN: Record<LibraryTabId, string> = {
  recents: 'searches',
  saves: 'saves',
  bottles: 'bottles',
}

interface Props extends LibraryLists {
  /** Keeps the dropdown's and the side menu's ids apart. */
  idPrefix: string
  /**
   * Rows a page holds before the list is laid out; after, a page is what fits
   * in its box.
   */
  pageSize: number
  /** Sizes the scrolling list, e.g. a max height. */
  listClassName?: string
  /**
   * `scroll`: one list that scrolls, its page controls following along.
   * `pages`: `pageSize` rows at a time, turned with the buttons, for a menu
   * that scrolls itself (a list scrolling inside it is awkward).
   */
  paging?: 'scroll' | 'pages'
  onPick?: (tab: LibraryTabId, entry: LibraryEntry, index: number) => void
  onTab?: (tab: LibraryTabId) => void
  onFilter?: (tab: LibraryTabId, filter: LibraryFilter | null) => void
  onPage?: (tab: LibraryTabId, page: number) => void
  /** Reads the next batch of recents when a page runs past what's loaded. */
  loadMoreRecents?: () => void
  loadingMoreRecents?: boolean
  /** The server-filtered recents for the active category, once read. */
  recentsByCategory?: RecentsByCategory | null
  /** Asks for a category's server-filtered recents (null: none). */
  setRecentsCategory?: (category: LibraryCategoryFilter | null) => void
  /** Recents read for the search box, once it opens. */
  recentsSearch?: RecentsSearch | null
  /** Asks for every recent (up to a cap) while a search is typed. */
  setSearchingRecents?: (on: boolean) => void
  className?: string
}

/**
 * The header Library's body, the same in the dropdown and the side menu:
 * Recents / Saves / Bottles tabs, a row of filters ("All Furniture Fashion
 * Fragrances", or "All Love Own Want"), a list that scrolls, and page controls
 * that follow the scroll ("1–10 of 57"; the arrows move a screenful). Recents
 * read their next batch as the list nears its end.
 * A tab with nothing in it isn't drawn.
 *
 * Recents and Saves filter by category over the rows read so far, reading
 * more recents until a filtered list fills. The api files each recent and
 * save under furniture, fashion or fragrance (`library_item_categories`);
 * an item it couldn't place (or hasn't yet) shows under "Other". `/api/recents` also takes
 * `?category=` for a server-filtered read. Every category is still offered.
 */
const LibraryPanel: React.FC<Props> = ({
  recents,
  recentsMore,
  saves,
  savesMore,
  loved,
  owned,
  wanted,
  idPrefix,
  pageSize,
  onPick,
  onTab,
  onFilter,
  onPage,
  loadMoreRecents,
  loadingMoreRecents = false,
  recentsByCategory,
  setRecentsCategory,
  recentsSearch,
  setSearchingRecents,
  className,
  listClassName,
  paging = 'scroll',
}) => {
  const paged = paging === 'pages'
  const utmParams = useCaptureUTMParams()
  const [picked, setPicked] = useState<LibraryTabId | null>(null)
  const [filters, setFilters] = useState<
    Record<LibraryTabId, LibraryFilter | null>
  >({ recents: null, saves: null, bottles: null })
  // Reads a filtered list has made on its own since the last click.
  const [autoLoads, setAutoLoads] = useState(0)
  const listRef = useRef<HTMLDivElement>(null)
  const [view, setView] = useState<ListView | null>(null)
  // `pages` only: the page shown, 0-based.
  const [shown, setShown] = useState(0)
  // One search box for every tab; Recents read all their rows once it's in use.
  const [searchOpen, setSearchOpen] = useState(false)
  const [query, setQuery] = useState('')
  const searching = searchOpen && isSearching(query)
  const searchButtonRef = useRef<HTMLButtonElement>(null)
  useEffect(() => () => setSearchingRecents?.(false), [setSearchingRecents])

  const entries = useMemo<Record<LibraryTabId, LibraryEntry[]>>(
    () => ({
      recents: recents.map(recentEntry),
      saves: saves.map((save, index) =>
        saveEntry(
          save,
          index,
          convertToRedirectUrl(
            save.url,
            { source: 'header-library' },
            utmParams
          )
        )
      ),
      bottles: bottleEntries({ loved, owned, wanted }),
    }),
    [recents, saves, loved, owned, wanted, utmParams]
  )

  const tabs = LIBRARY_TABS.filter((tab) => entries[tab].length > 0)
  const active = picked && tabs.includes(picked) ? picked : tabs[0]
  const filter = active ? filters[active] : null

  // A category on the Recents tab reads that category from the api, and pages
  // it there. Until that list arrives, the loaded rows are filtered here.
  const recentsCategory =
    !searching &&
    active === 'recents' &&
    LIBRARY_CATEGORY_FILTERS.includes(filter as LibraryCategoryFilter)
      ? (filter as LibraryCategoryFilter)
      : null
  useEffect(() => {
    setRecentsCategory?.(recentsCategory)
  }, [recentsCategory, setRecentsCategory])
  const server =
    recentsCategory && recentsByCategory?.category === recentsCategory
      ? recentsByCategory
      : null
  const serverEntries = useMemo(
    () => server?.recents.map(recentEntry) ?? null,
    [server?.recents]
  )

  // A search runs over the loaded recents plus every one read for it (the
  // loaded list first: it may be fresher), within the tab's filter.
  const searchRecents = useMemo(() => {
    if (!recentsSearch) return entries.recents
    const seen = new Set<string>()
    return [
      ...entries.recents,
      ...recentsSearch.recents.map(recentEntry),
    ].filter((e) => !seen.has(e.key) && Boolean(seen.add(e.key)))
  }, [entries.recents, recentsSearch?.recents])
  const searchBase =
    active === 'recents' ? searchRecents : active ? entries[active] : []
  // Only Recents with more than the loaded rows need the full read.
  const readAllRecents = searching && active === 'recents' && recentsMore
  useEffect(() => {
    setSearchingRecents?.(readAllRecents)
  }, [readAllRecents, setSearchingRecents])
  // Until the full read answers (and between its pages), it's still loading.
  const searchLoading =
    readAllRecents && (!recentsSearch || recentsSearch.loading)
  const searched = useMemo(
    () =>
      searching
        ? searchItems(
            searchBase.filter((e) => matchesFilter(e, filter)),
            query,
            (e) => e.search
          )
        : null,
    [searching, searchBase, filter, query]
  )
  const matching = searched
    ? searched
    : serverEntries
      ? serverEntries
      : active
        ? entries[active].filter((e) => matchesFilter(e, filter))
        : []
  // `loadMoreRecents` is also the "may read more now" switch (the side menu
  // passes it only while open), so the server list follows it too.
  const loadMore = server ? loadMoreRecents && server.loadMore : loadMoreRecents
  const loadingMore = server ? server.loading : loadingMoreRecents
  const canLoadMore =
    !searching &&
    active === 'recents' &&
    (server ? server.more : recentsMore) &&
    !!loadMore

  // Where the list is scrolled, read off the page. Before it is laid out
  // (and in tests, which lay nothing out) a page is `pageSize` rows.
  const measure = useCallback(() => {
    const el = listRef.current
    const row = el?.querySelector<HTMLElement>('[data-library-row]')
    if (paged || !el || !row?.offsetHeight || !el.clientHeight) {
      return setView(null)
    }
    const rowHeight = row.offsetHeight
    setView({
      rowHeight,
      perPage: Math.max(1, Math.floor(el.clientHeight / rowHeight)),
      inView: Math.max(1, Math.round(el.clientHeight / rowHeight)),
      first: Math.round(el.scrollTop / rowHeight),
      atTop: el.scrollTop <= 1,
      // Within two rows of the end: time to read the next batch.
      nearEnd:
        el.scrollTop + el.clientHeight >= el.scrollHeight - 2 * rowHeight,
      atEnd: el.scrollTop + el.clientHeight >= el.scrollHeight - 1,
    })
  }, [paged])

  useLayoutEffect(() => {
    measure()
  }, [measure, matching.length, active])

  useEffect(() => {
    const el = listRef.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => observer.disconnect()
  }, [measure, active])

  const rows = paged
    ? matching.slice(shown * pageSize, (shown + 1) * pageSize)
    : matching
  const perPage = view?.perPage ?? pageSize
  const first = view
    ? Math.min(view.first, matching.length)
    : paged
      ? shown * pageSize
      : 0
  const atEnd = view
    ? view.atEnd
    : (paged ? (shown + 1) * pageSize : pageSize) >= matching.length
  const fits = view ? view.atTop && view.atEnd : matching.length <= pageSize

  // Scrolled near the end (or a page or the box isn't full): read more.
  // A filter over the loaded rows, which most rows may miss, would read every
  // batch, so it stops after a few; a server-filtered list has no such cap.
  const needMore = view ? view.nearEnd : rows.length < pageSize
  const autoLoad =
    canLoadMore && (!filter || !!server || autoLoads < MAX_FILTER_LOADS)
  useEffect(() => {
    if (!needMore || !autoLoad || loadingMore) return
    if (filter && !server) setAutoLoads((n) => n + 1)
    loadMore?.()
  }, [needMore, autoLoad, filter, server, loadingMore, loadMore])

  // `pages`: a list that shrank (a filter, a save removed) keeps a real page.
  const lastPage = Math.max(0, Math.ceil(matching.length / pageSize) - 1)
  useEffect(() => {
    if (paged && shown > lastPage && !canLoadMore) setShown(lastPage)
  }, [paged, shown, lastPage, canLoadMore])

  if (!active) return null

  const counts: Record<LibraryTabId, string> = {
    recents: libraryCount(entries.recents.length, recentsMore),
    saves: libraryCount(entries.saves.length, savesMore),
    bottles: libraryCount(entries.bottles.length),
  }
  const bottleImage =
    [...loved, ...owned, ...wanted].find((b) => b.imageUrl)?.imageUrl ?? null

  // Bottles: the lists it has, each with its count. Categories: all of them.
  // While searching, each list counts its matches; a list stays offered
  // (at 0) so the row doesn't shift as the query changes.
  const all = entries[active]
  const counted = searching ? searchItems(all, query, (e) => e.search) : all
  const options = LIBRARY_TAB_FILTERS[active].flatMap((option) => {
    if (active !== 'bottles') return [{ filter: option }]
    if (!all.some((e) => matchesFilter(e, option))) return []
    const count = counted.filter((e) => matchesFilter(e, option)).length
    return [{ filter: option, count }]
  })
  // One bottle list only: filtering it would change nothing.
  const oneBottleList =
    active === 'bottles' &&
    BOTTLE_LISTS.filter((l) => ({ loved, owned, wanted })[l].length > 0)
      .length < 2

  const more = searching
    ? searchLoading
    : (active === 'recents' && (server ? server.more : recentsMore)) ||
      (active === 'saves' && savesMore)
  const page = paged
    ? shown
    : atEnd
      ? Math.max(0, Math.ceil(matching.length / perPage) - 1)
      : Math.floor(first / perPage)

  const toTop = () => {
    setShown(0)
    if (listRef.current) listRef.current.scrollTop = 0
  }
  const pickTab = (tab: LibraryTabId) => {
    if (tab !== active) onTab?.(tab)
    setPicked(tab)
    setAutoLoads(0)
    toTop()
  }
  const pickFilter = (next: LibraryFilter | null) => {
    if (next !== filter) onFilter?.(active, next)
    setFilters((now) => ({ ...now, [active]: next }))
    setAutoLoads(0)
    toTop()
  }
  const typeQuery = (next: string) => {
    setQuery(next)
    toTop()
  }
  const toggleSearch = () => {
    if (searchOpen) {
      setQuery('')
      // The box held focus: hand it back to the magnifier.
      searchButtonRef.current?.focus()
    }
    setSearchOpen(!searchOpen)
    toTop()
  }
  // The arrows scroll a screenful; reading on past the end is the scroll's job.
  const turn = (step: 1 | -1) => {
    onPage?.(active, Math.max(0, page + step))
    setAutoLoads(0)
    const el = listRef.current
    if (paged) {
      setShown(Math.max(0, shown + step))
    } else if (el && view) {
      el.scrollBy({ top: step * perPage * view.rowHeight, behavior: 'smooth' })
    } else if (step === 1 && canLoadMore) {
      loadMore?.()
    }
  }

  return (
    <div className={cn('flex min-h-0 flex-col', className)}>
      <div className='flex shrink-0 items-start gap-1 px-1 pb-3 pt-1'>
        <LibraryTabs
          tabs={tabs}
          active={active}
          counts={counts}
          bottleImage={bottleImage}
          onPick={pickTab}
          idPrefix={idPrefix}
          className='min-w-0 flex-1'
        />
        <LibrarySearchButton
          buttonRef={searchButtonRef}
          open={searchOpen}
          onToggle={toggleSearch}
          controls={`${idPrefix}-search`}
        />
      </div>
      {searchOpen && (
        <LibrarySearchInput
          id={`${idPrefix}-search`}
          value={query}
          onChange={typeQuery}
          onClose={toggleSearch}
          className='mx-1 mb-3 shrink-0'
        />
      )}
      <div
        role='tabpanel'
        id={libraryPanelId(idPrefix, active)}
        aria-labelledby={libraryTabId(idPrefix, active)}
        className='flex min-h-0 flex-1 flex-col border-t border-black/5 pt-2 dark:border-white/10'
      >
        {!oneBottleList && (
          <LibraryFilters
            options={options}
            value={filter}
            onChange={pickFilter}
            className='mb-2 shrink-0 px-1'
          />
        )}
        <div
          ref={listRef}
          onScroll={measure}
          className={cn(
            // Holds its height when a filter has few rows, so the menu doesn't jump.
            paged
              ? 'flex flex-col'
              : 'no-scrollbar flex min-h-[min(40dvh,22rem)] flex-1 flex-col overflow-y-auto overscroll-contain',
            listClassName
          )}
        >
          {rows.map((entry, index) => (
            <LibraryRow
              key={entry.key}
              entry={entry}
              onClick={() =>
                onPick?.(active, entry, (paged ? shown * pageSize : 0) + index)
              }
            />
          ))}
          {matching.length === 0 &&
            !autoLoad &&
            !(searching && searchLoading) && (
              <p className='px-2 py-3 text-sm text-foreground-secondary'>
                {searching
                  ? `No ${NOUN[active]} match “${query.trim()}”${filter ? ` in ${LIBRARY_FILTER_LABELS[filter]}` : ''}`
                  : filter === 'other'
                    ? `No other ${NOUN[active]} yet`
                    : `No ${NOUN[active]}${filter ? ` in ${LIBRARY_FILTER_LABELS[filter]}` : ''} yet`}
              </p>
            )}
          {searching && searchLoading && (
            <div className='flex h-14 shrink-0 items-center justify-center'>
              <Loader2 className='size-4 animate-spin opacity-50' />
            </div>
          )}
          {searching &&
            active === 'recents' &&
            !searchLoading &&
            !recentsSearch?.failed &&
            recentsSearch?.capped && (
              <p className='px-2 py-3 text-xs text-foreground-secondary'>
                Searched your latest {recentsSearch.recents.length} searches
              </p>
            )}
          {searching &&
            active === 'recents' &&
            readAllRecents &&
            recentsSearch?.failed && (
              <p className='px-2 py-3 text-xs text-foreground-secondary'>
                Couldn’t search all your searches.{' '}
                <button
                  type='button'
                  onClick={recentsSearch.retry}
                  className='underline underline-offset-2 hover:text-foreground'
                >
                  Try again
                </button>
              </p>
            )}
          {searching && active === 'saves' && savesMore && (
            <p className='px-2 py-3 text-xs text-foreground-secondary'>
              Searched your latest {saves.length} saves
            </p>
          )}
          {autoLoad && (rows.length === 0 || loadingMore) && (
            <div className='flex h-14 shrink-0 items-center justify-center'>
              <Loader2 className='size-4 animate-spin opacity-50' />
            </div>
          )}
        </div>
        {matching.length > 0 && !(fits && !canLoadMore) && (
          <LibraryPager
            first={first + 1}
            last={
              atEnd
                ? matching.length
                : Math.min(first + (view?.inView ?? perPage), matching.length)
            }
            total={matching.length}
            page={page}
            pages={Math.max(1, Math.ceil(matching.length / perPage))}
            more={more}
            canPrev={paged ? shown > 0 : view ? !view.atTop : false}
            canNext={!atEnd || canLoadMore}
            loading={loadingMoreRecents && active === 'recents' && atEnd}
            onPrev={() => turn(-1)}
            onNext={() => turn(1)}
            className='mt-2 border-t border-black/5 px-2 pt-2 dark:border-white/10'
          />
        )}
        {active === 'saves' && savesMore && (
          <Link
            href='/profile?tab=saves'
            className='px-2 pt-2 text-xs font-medium text-foreground-secondary hover:text-ink dark:hover:text-white'
          >
            Open your Saves page
          </Link>
        )}
      </div>
    </div>
  )
}

export default LibraryPanel
